import { CalendarDots } from "@phosphor-icons/react";
import clsx from "clsx";
import { useState } from "react";

import DatePicker from "common/components/DatePicker";
import { Button } from "common/components/ui/Button";
import {
  dropdownMenuContentClassname,
  dropdownMenuItemVariants,
  dropdownMenuListClassname,
} from "common/components/ui/DropdownMenu";
import FilterButton from "common/components/ui/FilterButton";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "common/components/ui/Popover";
import useCustomer from "common/datahooks/useCustomer";
import { formatDate, getMonthName } from "common/helpers/utils";
import type { MetricsParameters } from "common/types";

import { STATS_TIMEFRAME_OPTIONS } from "../../pages/campaigns/constants";

import type { DateRange } from "react-day-picker";

export default function TimeframeSelector({
  selectedTimeframe,
  selectTimeframe,
  shouldOffsetTimezone,
  className = null,
}: {
  selectedTimeframe: string | MetricsParameters;
  selectTimeframe: (selection: string | MetricsParameters) => void;
  shouldOffsetTimezone: boolean;
  className?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const { customerInfo } = useCustomer();

  const isCustomDate = typeof selectedTimeframe !== "string";

  function setSelectedRange(newSelection: DateRange) {
    selectTimeframe({
      from:
        newSelection.from.getTime() -
        (shouldOffsetTimezone
          ? newSelection.from.getTimezoneOffset() * 60 * 1000
          : 0),
      to:
        newSelection.to.getTime() -
        (shouldOffsetTimezone
          ? newSelection.to.getTimezoneOffset() * 60 * 1000
          : 0),
      bucketSize: "1 day",
    });
  }
  function onClear() {
    selectTimeframe(STATS_TIMEFRAME_OPTIONS[2].id);
  }

  let buttonLabel: string;

  if (isCustomDate) {
    const { from, to } = selectedTimeframe;
    const fromDate = new Date(from);
    const toDate = new Date(to);

    buttonLabel = `${getMonthName(fromDate.getMonth(), true)} ${fromDate.getDate()} - ${toDate.getMonth() === fromDate.getMonth() ? "" : getMonthName(toDate.getMonth(), true)} ${toDate.getDate()}`;
  } else {
    const selectedTimeframeOption = STATS_TIMEFRAME_OPTIONS.find(
      ({ id }) => id === selectedTimeframe,
    );
    buttonLabel = selectedTimeframeOption.label;
  }
  const minDate = new Date(customerInfo.created_at * 1000);
  const maxDate = new Date();

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <FilterButton leftIcon={<CalendarDots />} className={className}>
          {buttonLabel}
        </FilterButton>
      </PopoverTrigger>
      <PopoverContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        align="end"
        autoFocus={false}
        className={clsx(
          !isCustomDate && dropdownMenuContentClassname,
          "flex flex-col",
        )}
      >
        {isCustomDate ? (
          <>
            <div className="flex items-center justify-between border-b border-b-black-200 px-4 py-3 text-xs">
              {`${formatDate(selectedTimeframe.from)} - ${formatDate(selectedTimeframe.to)}`}
              <Button size="sm" variant="secondary-black" onClick={onClear}>
                Clear
              </Button>
            </div>
            <DatePicker
              mode="range"
              showOutsideDays
              selected={{
                from: new Date(selectedTimeframe.from),
                to: new Date(selectedTimeframe.to),
              }}
              onSelect={setSelectedRange}
              required
              startMonth={minDate}
              endMonth={maxDate}
              disabled={(date) =>
                date.getTime() < minDate.getTime() ||
                date.getTime() > maxDate.getTime()
              }
            />
          </>
        ) : (
          <div className={dropdownMenuListClassname}>
            <button
              type="button"
              onClick={() => {
                selectTimeframe({
                  from: Date.now(),
                  to: Date.now(),
                  bucketSize: "1 day",
                });
              }}
              className={dropdownMenuItemVariants({ variant: "default" })}
            >
              Custom
            </button>
            {STATS_TIMEFRAME_OPTIONS.map(({ id, label }) => (
              <button
                key={label}
                type="button"
                onClick={() => {
                  selectTimeframe(id);
                  setIsOpen(false);
                }}
                className={dropdownMenuItemVariants({ variant: "default" })}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
